import { useState } from "react";
import { Bot, CheckCircle2, Folder } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { ProjectAgentsDialog } from "./ProjectAgentsDialog";
import { ProjectDatasetsDialog } from "./ProjectDatasetsDialog";
import { useProjectContext } from "./useProjectContext";

interface ProjectSetupWizardProps {
  onContinue: () => void;
}

// Up to this many item pills render inline; the rest collapse into a "+N" tag.
const MAX_PREVIEW = 5;

export const ProjectSetupWizard = ({ onContinue }: ProjectSetupWizardProps) => {
  const { projectId, attachedAgents, attachedDatasets, isOwner } = useProjectContext();
  const [agentsDialogOpen, setAgentsDialogOpen] = useState(false);
  const [datasetsDialogOpen, setDatasetsDialogOpen] = useState(false);

  const agents = attachedAgents ?? [];
  const datasets = attachedDatasets ?? [];
  const hasAgents = agents.length > 0;
  const hasDatasets = datasets.length > 0;

  // A non-owner can't add agents or datasets, so the setup actions are useless
  // to them — show a hand-off message instead of dead buttons.
  if (!isOwner) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-8">
        <div className="flex w-full max-w-lg flex-col gap-2 text-center">
          <h1 className="text-xl font-semibold">This project isn&apos;t set up yet</h1>
          <p className="text-sm text-muted-foreground">
            Ask a project owner to add agents and datasets before you can start a session.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-8">
        <div className="flex w-full max-w-lg flex-col gap-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground">Get set up for your new session</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              To start a session, first add datasets and agents to the project.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <SetupCard
              icon={<Bot className="size-5" />}
              title="Add Agents"
              description="Choose one or more AI agents to use in this project"
              complete={hasAgents}
              onClick={() => setAgentsDialogOpen(true)}
            >
              {hasAgents && (
                <PreviewRow
                  items={agents.map((a) => ({
                    key: a.id,
                    label: a.display_name,
                    leading: <AgentAvatar id={a.id} name={a.name} size={18} />,
                  }))}
                />
              )}
            </SetupCard>

            <SetupCard
              icon={<Folder className="size-5" />}
              title="Add Datasets"
              description="Connect one or more datasets for your agents to work with"
              complete={hasDatasets}
              onClick={() => setDatasetsDialogOpen(true)}
            >
              {hasDatasets && (
                <PreviewRow
                  items={datasets.map((d) => ({
                    key: d.id,
                    label: d.title,
                    leading: (
                      <Folder className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    ),
                  }))}
                />
              )}
            </SetupCard>
          </div>

          <Button
            className="self-center px-8"
            disabled={!hasAgents || !hasDatasets}
            onClick={onContinue}
          >
            Start Session
          </Button>
        </div>
      </div>

      <ProjectAgentsDialog
        projectId={projectId}
        open={agentsDialogOpen}
        onOpenChange={setAgentsDialogOpen}
        attached={agents}
      />
      <ProjectDatasetsDialog
        projectId={projectId}
        open={datasetsDialogOpen}
        onOpenChange={setDatasetsDialogOpen}
        attachedIds={datasets.map((d) => d.id)}
      />
    </>
  );
};

interface SetupCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  complete: boolean;
  onClick: () => void;
  children?: React.ReactNode;
}

const SetupCard = ({ icon, title, description, complete, onClick, children }: SetupCardProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex w-full cursor-pointer flex-col gap-3 rounded-lg border px-5 py-4 text-left transition-colors hover:bg-accent/50",
      complete ? "border-green-500/40 bg-green-50/50 dark:bg-green-950/20" : "border-border"
    )}
  >
    <div className="flex w-full items-center gap-4">
      <div
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full",
          complete
            ? "bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-400"
            : "bg-muted text-muted-foreground"
        )}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {complete ? (
        <CheckCircle2 className="size-6 shrink-0 self-center text-green-600 dark:text-green-400" />
      ) : (
        <span className="shrink-0 text-xs font-medium text-primary">Add →</span>
      )}
    </div>
    {children}
  </button>
);

interface PreviewItem {
  key: string;
  label: string;
  leading: React.ReactNode;
}

// A horizontal run of pills showing what's been added so far, capped with a
// "+N" overflow tag. Indented to line up under the card's title.
const PreviewRow = ({ items }: { items: PreviewItem[] }) => {
  const shown = items.slice(0, MAX_PREVIEW);
  const overflow = items.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1.5 pl-14">
      {shown.map((item) => (
        <span
          key={item.key}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2 py-0.5"
        >
          {item.leading}
          <span className="max-w-[9rem] truncate text-xs text-foreground">{item.label}</span>
        </span>
      ))}
      {overflow > 0 && <span className="text-xs text-muted-foreground">+{overflow} more</span>}
    </div>
  );
};
